import React, { useEffect, useState } from 'react';
import { Autocomplete, type AutocompleteOption } from '@/components/ui/Autocomplete';
import { useLanguage } from '@/state/language/LanguageProvider';

type AlignmentTypeaheadProps = {
  value: string;
  onChange: (id: string) => void;
  options: AutocompleteOption[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
};

/**
 * Type-to-narrow picker for alignment entity links. Wraps Autocomplete so
 * each dialog only needs value + options, not a second search-state pair.
 */
export const AlignmentTypeahead: React.FC<AlignmentTypeaheadProps> = ({
  value,
  onChange,
  options,
  label,
  placeholder,
  disabled = false,
  autoFocus = false,
  className
}) => {
  const { t } = useLanguage();
  const [search, setSearch] = useState('');

  useEffect(() => {
    const selected = options.find((option) => option.id === value);
    if (selected) {
      setSearch(selected.label);
      return;
    }
    // External clear (e.g. Reports "Clear filters") wipes a matching label.
    // Mid-type filter text is kept so narrowing a long list stays usable.
    if (!value) {
      setSearch((current) => (options.some((option) => option.label === current) ? '' : current));
    }
  }, [value, options]);

  return (
    <Autocomplete
      value={value}
      onChange={onChange}
      onSelect={(option) => onChange(option?.id || '')}
      options={options}
      label={label}
      placeholder={placeholder || t('alignment.typeToSearch')}
      searchValue={search}
      onSearchChange={setSearch}
      disabled={disabled}
      autoFocus={autoFocus}
      className={className}
      loadingText={t('alignment.typeahead.loading')}
      noResultsText={t('alignment.typeahead.noResults')}
      noOptionsText={t('alignment.typeahead.noOptions')}
    />
  );
};
